import componentTemplate from 'decorators/componentTemplate';
import { wccModules } from 'enums/wccModules';
import { IconImageOwner } from 'interfaces/iconImageOwner';
import { inject, injectable } from 'inversify';
import { PureComputed, SubscribableOrNullableValue } from 'knockout';
import Badge from 'models/badge';
import EntityIcon from 'models/entityIcon';
import './entityImage.html';
import './entityImage.scss';

export interface EntityImageVMConfig {
    width: SubscribableOrNullableValue<number>
    height: SubscribableOrNullableValue<number>
    size: SubscribableOrNullableValue<number>
}

interface ExtendedEntityImageVMConfig<T extends IconImageOwner> extends EntityImageVMConfig {
    entity: SubscribableOrNullableValue<T>
}

@injectable()
@componentTemplate('wcc-components-entity-image')
export default abstract class EntityImageVM<T extends IconImageOwner> {
    imageURL: PureComputed<string | undefined>
    icon: PureComputed<EntityIcon | undefined>

    isImageShown: PureComputed<boolean>
    isIconShown: PureComputed<boolean>
    isCircleIcon: PureComputed<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: ExtendedEntityImageVMConfig<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const entity = ko.flattenComputed(config.entity);
        const width = ko.flattenComputed(config.width, config.size);
        const height = ko.flattenComputed(config.height, config.size);

        this.imageURL = entity.mapNotNull(entity => entity.imageURL());
        
        const entityBadge = entity.mapNotNull(entity => this.getBadge(entity));

        const entityIcon = entity.pluck(entity => {
            const iconClass = entity.iconClass();
            const iconColor = entity.iconColor();
            const iconStyle = entity.iconStyle();

            if (iconClass == undefined || iconColor == undefined)
                return undefined

            const isIconOnly = iconStyle === enums.ActivityIconStyles.Default.value;

            return new EntityIcon({
                icon: iconClass,
                color: iconColor,
                backgroundColor: !isIconOnly ? entity.iconBackgroundColor : undefined,
                borderColor: !isIconOnly ? entity.iconBorderColor : undefined,
                style: iconStyle
            });
        });

        const badgeIcon = ko.pureComputed(() => {
            const entityData = entity();
            const badgeData = entityBadge();

            if (entityData == undefined || badgeData == undefined)
                return undefined;

            const isIconOnly = entityData.iconStyle() === enums.ActivityIconStyles.Default.value;

            return new EntityIcon({
                icon: badgeData.icon,
                color: entityData.iconColor() ?? badgeData.color,
                backgroundColor: !isIconOnly ? entityData.iconBackgroundColor : undefined,
                borderColor: !isIconOnly ? entityData.iconBorderColor : undefined,
                style: entityData.iconStyle
            });
        });

        this.icon = ko.pureComputed(() => entityIcon() ?? badgeIcon());

        this.isImageShown = ko.pureComputed(() => entityIcon() == undefined && this.imageURL() != undefined);
        this.isIconShown = ko.pureComputed(() => !this.isImageShown() && this.icon() != undefined);
        this.isCircleIcon = ko.pureComputed(() => 
            !this.isImageShown() && this.icon() != undefined && this.icon()?.style() == enums.ActivityIconStyles.Circle.value);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'entity-image': true,
                'entity-image-icon': this.isIconShown() && !this.isCircleIcon(),
            },

            'style': {
                'width': width.toPx(true),
                'height': height.toPx(true)
            }
        }, this);
    }

    protected abstract getBadge(entity: T): Badge | undefined
}